'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  audienceOf,
  exposureFactor,
  revenueAtRisk,
  type LiveSite,
  type LiveSnapshot,
  type PlanCode,
  type PlansCatalogue,
} from '@content-removal/shared';
import { Notice } from '../components';
import { Waiting } from '../waiting';
import { useToast } from '../toast';
import type { PlanChoice } from '../plan-columns';
import { Radar } from './radar';
import { Cases, Spark } from './cases';
import { HowCard, LossCard } from './side';
import { FindingToasts, HistoryDrawer, ImproveModal, Paywall } from './overlays';

/** What the pages in a snapshot put at risk a month, as the loss card and the paywall both say it. */
export function riskOf(data: LiveSnapshot | null): { low: number; high: number } {
  if (!data) return { low: 0, high: 0 };
  const { monthlyLow, monthlyHigh } = revenueAtRisk(audienceOf([data.profile.followerRange]), exposureFactor(data.exposure));
  return { low: monthlyLow, high: monthlyHigh };
}

/** The app's own link, so the client app navigates and the admin preview does not leave the page. */
export type OverviewLink = React.ComponentType<{ href: string; className?: string; children: React.ReactNode }>;

/**
 * The client's live search, as the client sees it. One component for the
 * client app and for the admin preview, so the two can never show different
 * things: they differ only in where the snapshot comes from and what the
 * buttons do.
 */
export function LiveOverview({
  data,
  error = null,
  connected = true,
  plan,
  kind,
  owner = true,
  plans,
  profileHref,
  detectionsHref,
  historyHref,
  queuedAt = null,
  Link,
  onChoosePlan,
  onImprove,
}: {
  data: LiveSnapshot | null;
  error?: string | null;
  connected?: boolean;
  plan: PlanCode | null;
  /** Which plans the paywall offers: a creator never sees agency prices, nor the other way round. */
  kind: 'creator' | 'agency';
  /** A team member sees the plans but cannot buy one. */
  owner?: boolean;
  plans: { data: PlansCatalogue | null; error: string | null };
  profileHref: string;
  /** Where every finding is listed; without it the section has no "view all" link. */
  detectionsHref?: string;
  /** The full Scan History page; without it the history drawer has no footer link. */
  historyHref?: string;
  /** The profile's `firstScanQueuedAt`: whether a scan is on its way before any run exists. */
  queuedAt?: string | null;
  Link: OverviewLink;
  onChoosePlan: (choice: PlanChoice) => Promise<void>;
  onImprove: (names: string[], pages: string[]) => Promise<void>;
}) {
  const [paywall, setPaywall] = useState(false);
  const [improve, setImprove] = useState(false);
  const [history, setHistory] = useState(false);
  const [focus, setFocus] = useState<{ domain: string; at: number } | null>(null);
  const [fresh, setFresh] = useState<LiveSite[]>([]);
  const seen = useRef<Set<string> | null>(null);
  const toast = useToast();

  // A site is new to her the first time a snapshot carries it, not on the first paint.
  useEffect(() => {
    if (!data) return;
    const now = new Set(data.sites.map((site) => site.domain));
    if (seen.current) setFresh(data.sites.filter((site) => !seen.current!.has(site.domain)));
    seen.current = now;
  }, [data]);

  const risk = useMemo(() => riskOf(data), [data]);

  if (!data) return error ? <Notice tone="bad">{error}</Notice> : <Waiting />;

  const { scan, sites, totals, paying } = data;
  const running = scan.state === 'running';
  const failed = scan.state === 'failed';
  // No run row yet: nothing searched, so no counts and no "nothing found".
  const none = scan.state === 'none';
  const handleMissing = data.profile.handleExists === false;
  // A bar only where the run reports a total to measure against.
  const measured = running && scan.placesTotal > 0;
  const done = scan.placesTotal ? Math.min(1, scan.placesChecked / scan.placesTotal) : 0;
  const yours = kind === 'agency' ? 'their' : 'your';
  const unlock = () => setPaywall(true);
  // Only once the run row says complete, and not for an account holding a plan
  // whose payment is pending or failed: PaymentFailedNotice speaks for that one.
  const next = kind === 'agency' && !paying && plan === null && scan.state === 'complete';
  const open = (site: LiveSite) => (site.locked ? unlock() : setFocus({ domain: site.domain, at: Date.now() }));

  return (
    <>
      {error && !connected && <Notice tone="warn">Live updates paused; showing the last picture. {error}</Notice>}
      <FindingToasts fresh={fresh} onOpen={open} />
      {handleMissing && (
        <Notice tone="warn">
          We could not find an OnlyFans profile named @{data.profile.usernames[0]}, so no search was started. Please check the username.
        </Notice>
      )}

      <div className="ov-card ov-top">
        <div className="ov-top-head">
          <span className="ov-who">
            <span className="ov-avatar" aria-hidden="true"><svg viewBox="0 0 16 16"><circle cx="8" cy="5.6" r="2.6" /><path d="M3 13.5c.6-2.6 2.6-4 5-4s4.4 1.4 5 4" /></svg></span>
            <span className="ov-who-t">
              <b>{data.profile.stageName}</b>
              {data.profile.usernames[0] && <span>@{data.profile.usernames[0]}</span>}
            </span>
          </span>
          <span className="ov-who-meta">
            <span className="chip">{data.profile.photos} {data.profile.photos === 1 ? 'photo' : 'photos'}</span>
            <span className="chip">{data.profile.names} names</span>
          </span>
          <button type="button" className="btn btn-primary" onClick={() => (paying ? setImprove(true) : unlock())}>
            Improve results
          </button>
        </div>
        <div className="ov-top-main">
          <Radar sites={sites} running={running} protectedNow={paying} done={done} onPick={(index) => open(sites[index])} />
          <div className="ov-top-body">
            <div className="ov-title">
              <h2 className="ov-headline">
                {none
                  ? handleMissing
                    ? 'No scan started'
                    : queuedAt
                      ? 'First scan queued'
                      : 'No scan has run yet'
                  : running
                  ? `Searching the web for ${yours} content`
                  : failed
                    ? 'The last scan did not finish'
                    : paying
                    ? 'We are watching every site we found'
                    : sites.length
                      ? `Your leaked content is on ${sites.length} ${sites.length === 1 ? 'site' : 'sites'}`
                      : 'We have not found your content yet'}
              </h2>
              <span className="ov-live">
                <State state={none ? (queuedAt && !handleMissing ? 'queued' : 'idle') : scan.state} paying={paying} />
                <span className="ov-live-t">
                  {none
                    ? handleMissing
                      ? 'Fix the username to start the first scan.'
                      : queuedAt
                        ? 'It starts shortly and can take a while. This page updates by itself.'
                        : data.profile.usernames.length
                          ? 'It is picked up on the next scheduled check.'
                          : 'Add a username to the profile to start the first scan.'
                    : running
                      ? measured
                        ? `${scan.placesChecked.toLocaleString('en-US')} of ${scan.placesTotal.toLocaleString('en-US')} places checked`
                        : scan.startedAt ? `Started ${when(scan.startedAt)}` : ''
                      : failed
                        ? `Started ${when(scan.startedAt)}, stopped before it finished`
                        : paying && scan.nextAt
                          ? `watching ${sites.length} sites, next check ${nextIn(scan.nextAt)}`
                          : scan.startedAt ? `Last scan started ${when(scan.startedAt)}` : ''}
                </span>
              </span>
              {measured && <div className="ov-prog" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(done * 100)}><i style={{ width: `${done * 100}%` }} /></div>}
            </div>
            {!none && <div className="ov-stats">
              <div className="ov-stat is-hot">
                <StatIcon kind="pages" />
                <b>{totals.links}</b><span>pages with your content</span>
                <small>{running && totals.links ? `+${totals.links} found in this search` : `${totals.links - totals.inGoogle} not in Google, still online`}</small>
              </div>
              <div className="ov-stat"><StatIcon kind="sites" /><b>{totals.sites}</b><span>sites, one case each</span><small>each site gets its own notices</small></div>
              <div className="ov-stat is-warm"><StatIcon kind="google" /><b>{totals.inGoogle}</b><span>show up in Google</span><small>anyone searching your name finds these</small></div>
              <div className="ov-stat">
                <StatIcon kind="checked" />
                <b>{scan.placesChecked.toLocaleString('en-US')}</b><span>places checked</span>
                <small>{scan.engines} search {scan.engines === 1 ? 'engine' : 'engines'}, {scan.sites.toLocaleString('en-US')} sites</small>
              </div>
            </div>}
          </div>
        </div>
      </div>

      <div className="ov-cols">
        <div className="ov-card ov-main">
          <div className="ov-main-h">
            <h2>Sites found</h2>
            {detectionsHref && sites.length > 0 && (
              <Link href={detectionsHref} className="btn btn-sm">View detected pages</Link>
            )}
            <span className="ov-key"><span className="ov-ring is-mini" style={{ '--p': 96 } as React.CSSProperties}><span /></span>how sure we are it is your content</span>
          </div>
          {sites.length === 0 && none ? (
            <p className="ov-cap">Sites appear here as the first scan finds them.</p>
          ) : sites.length === 0 && failed ? (
            <p className="ov-cap">
              The scan stopped before it returned any pages. Scans run on a schedule, so there is nothing to start by hand;
              if this keeps happening, <Link href="/help">contact support</Link>.
            </p>
          ) : sites.length === 0 && !running ? (
            <p className="ov-cap">
              This scan returned no pages under {yours} names. That is not a guarantee nothing is out there: we keep searching on schedule.
            </p>
          ) : (
            <Cases sites={sites} running={running} focus={focus} onUnlock={unlock} />
          )}
          {data.history.length > 0 && (
            <button type="button" className="ov-hist" onClick={() => setHistory(true)} aria-haspopup="dialog">
              <span className="ov-hist-l">
                <b>Scan history</b>
                <span>
                  {data.history.length} {data.history.length === 1 ? 'search' : 'searches'}, the last one {new Date(data.history[0].startedAt).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </span>
              {data.history.length > 1 ? <Spark history={data.history} /> : <span />}
              <span className="ov-go">Open</span>
            </button>
          )}
        </div>
        <div className="ov-side">
          <LossCard low={risk.low} high={risk.high} exposure={data.exposure} profileHref={profileHref} Link={Link} />
          <HowCard plan={plan} paying={paying} sites={scan.sites} onUnlock={unlock} />
        </div>
      </div>

      {next ? (
        <div className="ov-dock">
          <div className="ov-dock-text">
            <b>Continue protecting your creators</b>
            <span>
              {data.history.length > 1 ? 'The latest scan is complete.' : 'Your first scan is complete.'} Choose an agency
              plan to continue protection and manage more creators through Snily.
            </span>
          </div>
          <button type="button" className="btn btn-primary" onClick={unlock}>View agency plans</button>
        </div>
      ) : !paying && sites.length > 0 && (
        <div className="ov-dock">
          <div className="ov-faces" aria-hidden="true">
            {sites.slice(0, 4).map((site) => <span key={site.domain} className={site.hot ? 'is-hot' : ''}>{site.locked ? '?' : site.domain[0]}</span>)}
            {sites.length > 4 && <span className="is-more">+{sites.length - 4}</span>}
          </div>
          <div className="ov-dock-text">
            <b>
              {totals.links} {totals.links === 1 ? 'page' : 'pages'} on {sites.length} {sites.length === 1 ? 'site' : 'sites'}
              {totals.inGoogle > 0 && <span className="chip chip-danger">{totals.inGoogle} in Google</span>}
            </b>
            <span>We can start sending notices today.</span>
          </div>
          <button type="button" className="btn btn-primary" onClick={unlock}>Remove all</button>
        </div>
      )}

      {paywall && (
        <Paywall
          sites={sites}
          pages={totals.links}
          inGoogle={totals.inGoogle}
          riskUsd={risk.high}
          kind={kind}
          title={next ? 'Agency plans' : undefined}
          plans={plans}
          onChoose={async (choice) => {
            if (!owner) return toast.info('Only the account owner can choose a plan. Ask them to open this from their account.');
            await onChoosePlan(choice);
          }}
          onClose={() => setPaywall(false)}
        />
      )}
      {improve && <ImproveModal profileHref={profileHref} onSave={onImprove} onClose={() => setImprove(false)} Link={Link} />}
      {history && <HistoryDrawer history={data.history} historyHref={historyHref} Link={Link} onClose={() => setHistory(false)} />}
    </>
  );
}

const STAT_ICONS = {
  pages: <><path d="M4 2.5h5l3 3v8H4z" /><path d="M9 2.5v3h3" /><path d="M6 9h4M6 11.2h3" /></>,
  sites: <><circle cx="8" cy="8" r="5.5" /><path d="M2.5 8h11" /><path d="M8 2.5c1.6 1.6 2.3 3.5 2.3 5.5S9.6 11.9 8 13.5C6.4 11.9 5.7 10 5.7 8S6.4 4.1 8 2.5z" /></>,
  google: <><circle cx="7" cy="7" r="4.2" /><path d="M10.1 10.1 13.5 13.5" /></>,
  checked: <><circle cx="8" cy="8" r="5.5" /><path d="M5.6 8.1l1.7 1.7 3.1-3.3" /></>,
  failed: <><circle cx="8" cy="8" r="5.5" /><path d="M8 5.2v3.4M8 10.8v.01" /></>,
  clock: <><circle cx="8" cy="8" r="5.5" /><path d="M8 5v3.2l2 1.3" /></>,
  radar: <><circle cx="8" cy="8" r="5.5" /><circle cx="8" cy="8" r="2.4" /><path d="M8 8l3.9-3.9" /></>,
  calendar: <><rect x="2.5" y="3.5" width="11" height="10" rx="2" /><path d="M2.5 6.8h11M5.5 2v3M10.5 2v3" /></>,
  chat: <><path d="M3 4.5A2 2 0 0 1 5 2.5h6a2 2 0 0 1 2 2V9a2 2 0 0 1-2 2H7.2L4.5 13.3V11H5a2 2 0 0 1-2-2z" /></>,
  removed: <><path d="M8 2l5 2v4c0 3-2.2 5.2-5 6-2.8-.8-5-3-5-6V4z" /><path d="M5.8 8.2l1.6 1.6 3-3.2" /></>,
};

/** A summary tile's corner icon; the tile it sits in needs `position: relative`. */
export function StatIcon({ kind }: { kind: keyof typeof STAT_ICONS }) {
  return (
    <span className="ov-stat-i" aria-hidden="true">
      <svg viewBox="0 0 16 16">{STAT_ICONS[kind]}</svg>
    </span>
  );
}

function State({ state, paying }: { state: LiveSnapshot['scan']['state'] | 'idle' | 'queued'; paying: boolean }) {
  if (state === 'queued' || state === 'idle' || state === 'none') {
    return (
      <span className="ov-state is-queued">
        <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="5.5" /><path d="M8 5v3.2l2 1.3" /></svg>
        {state === 'queued' ? 'Queued' : 'Not started'}
      </span>
    );
  }
  if (state === 'failed') {
    return (
      <span className="ov-state is-open">
        <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="5.5" /><path d="M8 5.2v3.4M8 10.8v.01" /></svg>Scan failed
      </span>
    );
  }
  if (state === 'running') {
    return (
      <span className="ov-state is-running">
        <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M13.5 8a5.5 5.5 0 1 1-2-4.2" /></svg>Searching
      </span>
    );
  }
  if (paying) {
    return (
      <span className="ov-state is-safe">
        <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2l5 2v4c0 3-2.2 5.2-5 6-2.8-.8-5-3-5-6V4z" /><path d="M5.8 8.2l1.6 1.6 3-3.2" /></svg>Protected
      </span>
    );
  }
  return (
    <span className="ov-state is-open">
      <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2l5 2v4c0 3-2.2 5.2-5 6-2.8-.8-5-3-5-6V4z" /><path d="M8 5.5v3" /><path d="M8 10.8v.2" /></svg>Not protected
    </span>
  );
}

function when(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function nextIn(iso: string): string {
  const minutes = Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 60_000));
  if (minutes >= 48 * 60) return `in ${Math.round(minutes / (24 * 60))} days`;
  return `in ${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}
